import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'

import { useMutateChangeOrderSlot } from '@entities/Order/api/changeOrderSlot.gql'
import { type Slot, useAvailableSlots } from '@entities/Slots'

import { useErrorInfoModal, useModalStore } from '@features/modal'
import { useAddressStore, useDelivery } from '@features/shipping-methods'

import TabsSwitcher from '@widgets/modal-delivery/ui/TabsSwitcher'
import { useSlotStore } from '@widgets/slots/model/useSlotStore'

import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import { useToastStore } from '@shared/ui/toast/useToastStore'
import { formatPrice } from '@shared/uttils'

import styles from './styles.module.scss'

interface SlotsModalProps {
  orderId?: string | number
  onUpdate?: () => void
}

export default function SlotsModal ({ orderId, onUpdate }: SlotsModalProps = {}) {
  const { t: tc } = useTranslation('common')
  const { t: tca } = useTranslation('cart')
  const { t: te } = useTranslation('errors')
  const [ activeDate, setActiveDate ] = useState<string>('')
  const { selectedSlot, setSelectedSlot } = useSlotStore()
  
  // Используем useDelivery для получения данных о доставке
  const { freeSlotDeliveryCostFrom, deliveryCost } = useDelivery()
  const { shopId, zoneId, deliveryType, isPickup } = useAddressStore()
  
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { closeModal } = useModalStore()
  const [ selectedLocalSlot, setSelectedLocalSlot ] = useState<Slot | null>(null)
  const { openErrorInfoModal } = useErrorInfoModal()
  const addToast = useToastStore((state) => state.addToast)
  const [ changeOrderSlot, { loading: updateLoading } ] = useMutateChangeOrderSlot()

  // Используем entity хук для слотов
  const { slots, error: slotsError } = useAvailableSlots({
    shopId: deliveryType === 'pickup' ? shopId : undefined,
    zoneId: deliveryType === 'delivery' ? zoneId : undefined,
  })

  useEffect(() => {
    if (slotsError) {
      openErrorInfoModal({ description: te('get_slots_data') })
    }
  }, [ slotsError, openErrorInfoModal, te ])

  // Группируем слоты по дате
  const SLOTS_GROUP = useMemo(() => {
    if (!slots || slots.length === 0) return undefined
    const group: Record<string, Slot[]> = {}
    slots.forEach((slot) => {
      const key = slot.slotDate
      if (!key) return
      if (group[key]) group[key].push(slot)
      else group[key] = [ slot ]
    })
    return group
  }, [ slots ])

  useEffect(() => {
    if (!SLOTS_GROUP) return

    const firstDate = Object.keys(SLOTS_GROUP)[0]

    if (selectedSlot) {
      setActiveDate(selectedSlot.slotDate as string)
      setSelectedLocalSlot(selectedSlot)
    }
    else {
      setActiveDate((prev) => prev || firstDate)

      const firstSlot = SLOTS_GROUP[firstDate]?.[0]
      if (firstSlot) {
        setSelectedLocalSlot(firstSlot)
        setSelectedSlot(firstSlot)
      }
    }
  }, [ SLOTS_GROUP, selectedSlot, setSelectedSlot ])
  const dateTabs = useMemo(() => {
    if (!SLOTS_GROUP) return []
    return Object.keys(SLOTS_GROUP).map((date) => ({
      label: date,
      value: date,
    }))
  }, [ SLOTS_GROUP ])

  useEffect(() => {
    if (SLOTS_GROUP) {
      setActiveDate(Object.keys(SLOTS_GROUP)[0])
    }
  }, [ SLOTS_GROUP ])

  return (
    <div className={styles.root}>
      <div className={styles.root__group}>
        {!isPickup() && freeSlotDeliveryCostFrom > 0 && (
          <div className={styles.cost}>
            <ReactSVG
              className={styles['cost__icon']}
              src="/icons/gift.svg"
              wrapper="span"
            />
            <div className={styles['cost__wrapper']}>
              <p className={styles['cost__title']}>{tca('delivery.free')}</p>
              {deliveryCost ? (
                <p className={styles['cost__price']}>
                  {tca('delivery.amount_to', {
                    amount: formatPrice(freeSlotDeliveryCostFrom),
                  })}
                  {/* при заказе от{" "} */}
                  {/* <span>{formatPrice(freeSlotDeliveryCostFrom)}</span> */}
                </p>
              ) : null}
            </div>
          </div>
        )}

        <h3 className={styles['root__group-title']}>{tc('slots.date')}</h3>
        <TabsSwitcher
          tabs={dateTabs}
          active={activeDate}
          cols={Object.keys(SLOTS_GROUP || {}).length}
          onChange={setActiveDate}
        />
      </div>

      <div className={styles.root__group}>
        <h3 className={styles['root__group-title']}>{tc('slots.time')}</h3>

        <div className={styles.root__list}>
          {SLOTS_GROUP?.[activeDate]?.map((slot) => (
            <div
              className={cn(styles.root__slot, {
                [styles.active]: selectedLocalSlot?.id === slot?.id,
              })}
              key={slot?.id}
              onClick={() => setSelectedLocalSlot(slot)}
            >
              <div className={styles['root__slot-name']}>
                {slot?.slotDate} {tc('from')} {slot?.slotTime}
              </div>
              <div className={styles['root__slot-check']}>
                <ReactSVG src="/icons/circle-check-filled.svg" />
              </div>
            </div>
          ))}
        </div>

        <Button
          size={isTablet ? 'medium' : 'large'}
          disabled={updateLoading}
          onClick={async () => {
            if (!selectedLocalSlot) return

            // Если передан orderId, обновляем существующий заказ
            if (orderId) {
              try {
                const result = await changeOrderSlot({
                  variables: {
                    order_id: String(orderId),
                    slot_id: String(selectedLocalSlot.id),
                  },
                })
                
                if (result.data?.changeOrderSlot.slot_id == selectedLocalSlot.id) {
                  addToast({
                    variant: 'success',
                    message: tc('slots.updated'),
                  })
                }
                
                if (onUpdate) {
                  onUpdate()
                }
                
                closeModal('slots')
              }
              catch {
                addToast({
                  variant: 'error',
                  message: te('update_slot'),
                })
              }
            }
            else {
              // Для нового заказа просто сохраняем в store
              setSelectedSlot(selectedLocalSlot)
              closeModal('slots')
            }
          }}
        >
          {tc('choose')}
        </Button>
      </div>
    </div>
  )
}
